import React, { useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import MyPagination from 'components/ui/MyPagination'
import MyRefetchButton from 'components/ui/MyRefetchButton'
import TemplateCreate from 'components/template-create'
import Selector from 'components/ui/Selector'
import { useForm } from './index'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { X } from 'lucide-react'
import { Checkbox } from '@/components/ui/checkbox'

const STATE_OPTIONS = [
  { code: 0, name: 'Draft' },
  { code: 200, name: 'InReview' },
  { code: 300, name: 'Reject' },
  { code: 100, name: 'Scheduled' },
  { code: 401, name: 'Online' },
  { code: 600, name: 'Offline' },
]

const DataTableToolbar = ({ mutate, nextAfter }) => {
  const [, setSearchParams] = useSearchParams()
  const afters = useForm((s) => s.afters)
  const after = useForm((s) => s.after)
  const goAfter = useForm((s) => s.goAfter)
  const tid = useForm((s) => s.tid)
  const setTid = useForm((s) => s.setTid)
  const publisher = useForm((s) => s.publisher)
  const setPublisher = useForm((s) => s.setPublisher)
  const status = useForm((s) => s.status)
  const setStatus = useForm((s) => s.setStatus)
  const aspectRatio = useForm((s) => s.aspectRatio)
  const setAspectRatio = useForm((s) => s.setAspectRatio)
  const hasDlc = useForm((s) => s.hasDlc)
  const setHasDlc = useForm((s) => s.setHasDlc)

  const hasStatus = status !== null && status !== undefined && status !== ''
  const isMultiTid =
    String(tid || '')
      .split(/[\s,，;；]+/)
      .filter((item) => item.trim().length > 0).length > 1

  const handleTidChange = (e) => {
    setTid(e.target.value)
  }

  const handleClearTid = () => {
    setTid('')
  }

  const syncPublisher = useCallback(
    (value) => {
      const next = value || ''
      setPublisher(next)
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev)
          if (next) params.set('publisher', next)
          else params.delete('publisher')
          return params
        },
        { replace: true },
      )
    },
    [setPublisher, setSearchParams],
  )

  const handlePublisherChange = (e) => {
    syncPublisher(e.target.value)
  }

  const handleClearPublisher = () => {
    syncPublisher('')
  }

  const handleHasDlcChange = (checked) => {
    setHasDlc(checked === true)
  }

  return (
    <div className="mb-2 flex w-full flex-wrap items-center justify-between gap-y-2">
      <div className="flex flex-wrap items-center gap-4">
        {!isMultiTid && (
          <MyPagination
            nextAfter={nextAfter}
            after={after}
            goAfter={goAfter}
            afters={afters}
          />
        )}
        <MyRefetchButton onClick={mutate} />
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <label htmlFor="tid-filter" className="text-sm font-medium">
              TID:
            </label>
            <div className="relative">
              <Input
                id="tid-filter"
                type="text"
                placeholder="TID，多个用逗号或空格分隔"
                value={tid}
                onChange={handleTidChange}
                className="w-[28rem] max-w-full pr-8"
              />
              {tid && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearTid}
                  className="absolute right-1 top-1/2 h-6 w-6 -translate-y-1/2 p-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <label htmlFor="aspect-ratio-filter" className="text-sm font-medium">画幅:</label>
            <Selector id="aspect-ratio-filter" labelName="画幅" width="w-[130px]"
              datas={[{ code: 'all', name: '全部' }, { code: '0', name: '1:1' }, { code: '2', name: '9:16' }, { code: '3', name: '3:4' }]}
              value={aspectRatio === '' ? 'all' : aspectRatio}
              update={(value) => setAspectRatio(value === 'all' ? '' : String(value))} />
          </div>

          <div className="flex items-center gap-2">
            <label htmlFor="publisher-filter" className="text-sm font-medium">
              Publisher:
            </label>
            <div className="relative">
              <Input
                id="publisher-filter"
                type="text"
                placeholder="Publisher"
                value={publisher}
                onChange={handlePublisherChange}
                className="w-48"
              />
              {publisher && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearPublisher}
                  className="absolute right-1 top-1/2 h-6 w-6 -translate-y-1/2 p-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <label htmlFor="status-filter" className="text-sm font-medium">
              State:
            </label>
            <div className="relative">
              <Selector
                id="status-filter"
                datas={STATE_OPTIONS}
                labelName="State"
                value={hasStatus ? status : null}
                update={setStatus}
                placeholder="All"
              />
              {hasStatus ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  title="Clear state"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    setStatus(null)
                  }}
                  className="absolute right-8 top-1/2 h-6 w-6 -translate-y-1/2 p-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              ) : null}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="has-dlc-filter"
              checked={hasDlc}
              onCheckedChange={handleHasDlcChange}
            />
            <label
              htmlFor="has-dlc-filter"
              className="cursor-pointer text-sm font-medium leading-none"
            >
              仅含已发布 DLC（画幅与 DLC 暂不支持同时筛选）
            </label>
          </div>
        </div>
      </div>
      <TemplateCreate mutate={mutate} />
    </div>
  )
}

export default DataTableToolbar
